import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import LanguageSelector from "@/components/LanguageSelector";
import { LandingCountryBanner, useLandingCountry } from "@/components/LandingCountryBanner";
import LandingCountryFiscal from "@/components/LandingCountryFiscal";
import LandingSEO from "@/components/LandingSEO";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Users, 
  DollarSign, 
  Clock, 
  Calendar, 
  Target, 
  Briefcase, 
  BarChart3, 
  Check, 
  X,
  ArrowRight,
  Shield,
  Zap,
  Globe,
  Rocket,
  Crown,
  MapPin,
  Mail,
  Phone,
  Clock3,
  Menu,
  Receipt,
  Bell,
  FileText,
  Network,
  Wallet,
  BookOpen,
  Building2,
  Brain,
  FileBarChart,
  Smartphone,
  Search,
  HeadphonesIcon,
  ChevronDown,
  Calculator,
  Percent,
  Award,
  TrendingUp,
  UserPlus,
  Play,
  Star,
  Quote,
  HelpCircle,
  CheckCircle,
  Sparkles,
  Cloud as CloudIcon,
  Palette,
  FileSignature,
  GitBranch,
  Landmark
} from "lucide-react";

// Icons will be used dynamically in the component
// Feature, Testimonial, and FAQ data will be generated using translations

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const landingCountry = useLandingCountry();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    pricing: true, hr: true, payroll: true, talent: true, integrations: true, admin: true
  });
  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  
  // Generate features array using translations
  const features = [
    { icon: Users, titleKey: "employees", descKey: "employeesDesc" },
    { icon: DollarSign, titleKey: "payroll", descKey: "payrollDesc" },
    { icon: MapPin, titleKey: "geoGps", descKey: "geoGpsDesc", isNew: true },
    { icon: Globe, titleKey: "realtimeMap", descKey: "realtimeMapDesc", isNew: true },
    { icon: Shield, titleKey: "fraudDetection", descKey: "fraudDetectionDesc", isNew: true },
    { icon: Bell, titleKey: "autoAlerts", descKey: "autoAlertsDesc", isNew: true },
    { icon: FileBarChart, titleKey: "advancedReports", descKey: "advancedReportsDesc" },
    { icon: Brain, titleKey: "aiSearch", descKey: "aiSearchDesc" },
    { icon: Smartphone, titleKey: "portal", descKey: "portalDesc" },
    { icon: Receipt, titleKey: "expenses", descKey: "expensesDesc" },
    { icon: Wallet, titleKey: "loans", descKey: "loansDesc" },
    { icon: Clock, titleKey: "attendance", descKey: "attendanceDesc" },
    { icon: Calendar, titleKey: "vacations", descKey: "vacationsDesc" },
    { icon: Target, titleKey: "evaluations", descKey: "evaluationsDesc" },
    { icon: Briefcase, titleKey: "recruitment", descKey: "recruitmentDesc" },
    { icon: Network, titleKey: "orgChart", descKey: "orgChartDesc" },
    { icon: BookOpen, titleKey: "accounting", descKey: "accountingDesc" },
    { icon: CloudIcon, titleKey: "quickbooks", descKey: "quickbooksDesc" },
    { icon: FileText, titleKey: "dgii", descKey: "dgiiDesc" },
    { icon: Palette, titleKey: "customization", descKey: "customizationDesc" },
    { icon: FileSignature, titleKey: "contracts", descKey: "contractsDesc", isNew: true },
    { icon: GitBranch, titleKey: "workflows", descKey: "workflowsDesc", isNew: true },
    { icon: Landmark, titleKey: "achBank", descKey: "achBankDesc", isNew: true },
  ];
  
  // Generate testimonials array using translations
  const testimonials = [
    { id: "1", image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face", rating: 5 },
    { id: "2", image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face", rating: 5 },
    { id: "3", image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=face", rating: 5 },
    { id: "4", image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face", rating: 5 },
  ];
  
  // Generate FAQs array using translations  
  const faqIds = ["1", "2", "3", "4", "5", "6", "7", "8"];

  // Features for dropdowns
  const empresaFeatures = [
    { icon: Users, title: t('landing.features.employees'), desc: t('landing.features.employeesDesc').substring(0, 40) + "..." },
    { icon: DollarSign, title: t('landing.features.payroll'), desc: t('landing.features.payrollDesc') },
    { icon: Clock, title: t('landing.features.attendance'), desc: t('landing.features.attendanceDesc').substring(0, 40) + "..." },
    { icon: Calendar, title: t('landing.features.vacations'), desc: t('landing.features.vacationsDesc').substring(0, 40) + "..." },
    { icon: Target, title: t('landing.features.evaluations'), desc: t('landing.features.evaluationsDesc').substring(0, 40) + "..." },
    { icon: FileBarChart, title: t('landing.features.advancedReports'), desc: t('landing.features.advancedReportsDesc').substring(0, 40) + "..." },
    { icon: Brain, title: t('landing.features.aiSearch'), desc: t('landing.features.aiSearchDesc').substring(0, 40) + "..." },
    { icon: Smartphone, title: t('landing.features.portal'), desc: t('landing.features.portalDesc').substring(0, 40) + "..." },
    { icon: FileSignature, title: t('landing.features.contracts'), desc: t('landing.features.contractsDesc').substring(0, 40) + "..." },
    { icon: GitBranch, title: t('landing.features.workflows'), desc: t('landing.features.workflowsDesc').substring(0, 40) + "..." },
    { icon: Landmark, title: t('landing.features.achBank'), desc: t('landing.features.achBankDesc').substring(0, 40) + "..." }
  ];

  const contadoresFeatures = [
    { icon: DollarSign, title: "$10" + t('accountants.pricePerMonth'), desc: t('landing.pricing.perEmployee') },
    { icon: Percent, title: "30%", desc: t('landing.accountantDropdown.commission') },
    { icon: Building2, title: t('landing.accountantDropdown.multiClient'), desc: t('landing.pricing.enterpriseDesc') },
    { icon: TrendingUp, title: "Dashboard", desc: t('landing.accountantDropdown.dashboard') },
    { icon: Calculator, title: t('landing.features.payroll'), desc: "TSS, AFP, ISR" },
    { icon: FileText, title: t('landing.features.dgii'), desc: "IR-17, TSS" }
  ];

  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Ensure full content renders on first load
  useEffect(() => {
    // Force scroll to top on mount to ensure proper hydration
    window.scrollTo(0, 0);
    // Force a small layout recalculation
    document.body.style.overflow = 'auto';
  }, []);

  // Scroll reveal effect
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    // Observe all scroll-reveal elements
    const revealElements = document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale');
    revealElements.forEach((el) => observer.observe(el));

    return () => {
      revealElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <LandingSEO
        profile={landingCountry.profile}
        countryCode={landingCountry.countryCode}
        lang={(i18n.language || "es").split("-")[0]}
      />      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-700" style={{ backgroundColor: '#0f172a' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_hr-dominican/artifacts/3k6l3c5h_FortexaRH_Logo_sin%20fondo%201.png" 
                alt="FortexaRH" 
                className="h-16 sm:h-20 md:h-24 w-auto"
              />
            </Link>
            
            {/* Desktop Navigation with Dropdowns */}
            <nav className="hidden md:flex items-center gap-1 lg:gap-2" ref={dropdownRef}>
              {/* Para Empresas Dropdown */}
              <div className="relative">
                <button 
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm lg:text-base font-medium rounded-lg transition-colors ${
                    openDropdown === 'empresas' 
                      ? 'text-white bg-slate-700' 
                      : 'text-white hover:bg-slate-800'
                  }`}
                  onClick={() => setOpenDropdown(openDropdown === 'empresas' ? null : 'empresas')}
                  data-testid="nav-empresas"
                >
                  {t('landing.nav.forCompanies')}
                  <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'empresas' ? 'rotate-180' : ''}`} />
                </button>
                
                {openDropdown === 'empresas' && (
                  <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 py-3 animate-fade-in z-50">
                    <div className="px-4 pb-2 mb-2 border-b border-slate-100">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('landing.nav.forCompanies')}</p>
                    </div>
                    {empresaFeatures.map((feature, idx) => (
                      <a 
                        key={idx}
                        href="#features"
                        className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <feature.icon className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{feature.title}</p>
                          <p className="text-xs text-slate-500">{feature.desc}</p>
                        </div>
                      </a>
                    ))}
                    <div className="px-4 pt-3 mt-2 border-t border-slate-100">
                      <Link 
                        to="/register"
                        className="flex items-center justify-center gap-2 w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors"
                        onClick={() => setOpenDropdown(null)}
                      >
                        {t('landing.pricing.freeTrial')}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Para Contadores Dropdown */}
              <div className="relative">
                <button 
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm lg:text-base font-medium rounded-lg transition-colors ${
                    openDropdown === 'contadores' 
                      ? 'text-white bg-slate-700' 
                      : 'text-white hover:bg-slate-800'
                  }`}
                  onClick={() => setOpenDropdown(openDropdown === 'contadores' ? null : 'contadores')}
                  data-testid="nav-contadores"
                >
                  {t('landing.nav.accountants')}
                  <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'contadores' ? 'rotate-180' : ''}`} />
                </button>
                
                {openDropdown === 'contadores' && (
                  <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 py-3 animate-fade-in z-50">
                    <div className="px-4 pb-2 mb-2 border-b border-slate-100">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('landing.footer.accountantProgram')}</p>
                    </div>
                    {contadoresFeatures.map((feature, idx) => (
                      <Link 
                        key={idx}
                        to="/accountants-software"
                        className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <feature.icon className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{feature.title}</p>
                          <p className="text-xs text-slate-500">{feature.desc}</p>
                        </div>
                      </Link>
                    ))}
                    <div className="px-4 pt-3 mt-2 border-t border-slate-100">
                      <Link 
                        to="/accountants-software"
                        className="flex items-center justify-center gap-2 w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <Award className="w-4 h-4" />
                        {t('landing.footer.accountantProgram')}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Simple Links */}
              <a 
                href="#pricing" 
                className="px-4 py-2 text-sm lg:text-base font-medium text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                {t('landing.nav.pricing')}
              </a>
              <a 
                href="#contact" 
                className="px-4 py-2 text-sm lg:text-base font-medium text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                {t('landing.nav.contact')}
              </a>
            </nav>
            
            {/* Desktop Buttons */}
            <div className="hidden md:flex items-center gap-2 lg:gap-3">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-sm font-medium text-white hover:bg-slate-800" data-testid="login-btn">
                  {t('landing.nav.login')}
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-sm" data-testid="register-btn">
                  {t('landing.nav.startFree')}
                </Button>
              </Link>
              
              {/* Language Selector */}
              <LanguageSelector variant="landing" />
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 -mr-2 text-slate-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-800 border-t border-slate-700 py-4 px-4 animate-fade-in">
            <nav className="flex flex-col gap-2 mb-4">
              {/* Mobile - Para Empresas */}
              <div className="py-2">
                <button 
                  className="flex items-center justify-between w-full text-slate-300 py-2 px-3 rounded-lg"
                  onClick={() => setOpenDropdown(openDropdown === 'empresas-mobile' ? null : 'empresas-mobile')}
                >
                  <span className="font-medium">{t('landing.nav.forCompanies')}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'empresas-mobile' ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === 'empresas-mobile' && (
                  <div className="mt-2 ml-3 pl-3 border-l-2 border-slate-600">
                    {empresaFeatures.slice(0, 4).map((feature, idx) => (
                      <a 
                        key={idx}
                        href="#features"
                        className="flex items-center gap-2 py-2 text-sm text-slate-400 hover:text-white"
                        onClick={() => { setOpenDropdown(null); setMobileMenuOpen(false); }}
                      >
                        <feature.icon className="w-4 h-4 text-emerald-400" />
                        {feature.title}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Mobile - Para Contadores */}
              <div className="py-2">
                <button 
                  className="flex items-center justify-between w-full text-slate-300 py-2 px-3 rounded-lg"
                  onClick={() => setOpenDropdown(openDropdown === 'contadores-mobile' ? null : 'contadores-mobile')}
                >
                  <span className="font-medium">{t('landing.nav.accountants')}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'contadores-mobile' ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === 'contadores-mobile' && (
                  <div className="mt-2 ml-3 pl-3 border-l-2 border-slate-600">
                    {contadoresFeatures.slice(0, 4).map((feature, idx) => (
                      <Link 
                        key={idx}
                        to="/accountants-software"
                        className="flex items-center gap-2 py-2 text-sm text-slate-400 hover:text-white"
                        onClick={() => { setOpenDropdown(null); setMobileMenuOpen(false); }}
                      >
                        <feature.icon className="w-4 h-4 text-emerald-400" />
                        {feature.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              
              <a 
                href="#pricing" 
                className="text-slate-300 hover:text-white py-2 px-3 rounded-lg hover:bg-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('landing.nav.pricing')}
              </a>
              <a 
                href="#contact" 
                className="text-slate-300 hover:text-white py-2 px-3 rounded-lg hover:bg-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('landing.nav.contact')}
              </a>
            </nav>
            <div className="flex flex-col gap-2 pt-3 border-t border-slate-700">
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-700">{t('landing.nav.login')}</Button>
              </Link>
              <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600">{t('landing.nav.startFree')}</Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Landing Country Banner — global selector */}
      <div className="pt-16 sm:pt-20">
        <LandingCountryBanner {...landingCountry} t={t} />
      </div>

      {/* Hero Section - Enhanced with Video */}
      <section className="pt-8 sm:pt-10 md:pt-12 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="animate-fade-in text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6 border border-emerald-200" data-testid="hero-tagline">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                #1 {t('landing.hero.title', { country: landingCountry.profile?.name || 'República Dominicana' })}
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight heading mb-4 sm:mb-6">
                {t('landing.features.payroll')}
                <span className="text-emerald-600 block sm:inline"> {t('landing.hero.subtitle').split('.')[0]}</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-600 mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0">
                {t('landing.hero.subtitle')}
                {!landingCountry.isDefault && landingCountry.profile && (
                  <span className="block mt-2 text-sm text-emerald-700 font-semibold">
                    ✓ {t('landing.countryFiscal.title', { country: landingCountry.profile.name })} · {landingCountry.profile.currency_symbol} {landingCountry.profile.currency}
                  </span>
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Link to="/register" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-sm sm:text-base px-6 sm:px-8 shadow-lg shadow-emerald-200" data-testid="hero-cta-btn">
                    {t('landing.hero.cta')}
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#demo-video" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-sm sm:text-base px-6 sm:px-8 border-slate-300 hover:bg-slate-50" data-testid="watch-demo-btn">
                    <Play className="w-4 h-4 mr-2" />
                    {t('landing.hero.demo')}
                  </Button>
                </a>
                <Link to="/brochure" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-sm sm:text-base px-6 sm:px-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="brochure-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Brochure
                  </Button>
                </Link>
                <Link to="/calculator" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-sm sm:text-base px-6 sm:px-8 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="landing-calculator-btn">
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculadora gratis
                  </Button>
                </Link>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-6 mt-6 sm:mt-8 text-xs sm:text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
                  {t('landing.pricing.freeTrial')}
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
                  {t('common.cancel')}
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
                  {t('landing.contact.supportCenter')}
                </div>
              </div>
            </div>
            <div className="relative animate-fade-in stagger-2 hidden sm:block">
              <div className="bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl">
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
                </div>
                <img 
                  src="https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwY29ycG9yYXRlJTIwdGVhbSUyMHdvcmtpbmclMjBpbiUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3Njg2ODM3Mzd8MA&ixlib=rb-4.1.0&q=85"
                  alt="Equipo corporativo trabajando"
                  className="rounded-lg w-full h-48 sm:h-56 md:h-64 object-cover"
                />
              </div>
              <div className="absolute -bottom-4 sm:-bottom-6 -left-2 sm:-left-6 bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-lg border border-slate-100 pointer-events-none">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-lg sm:text-2xl font-bold text-slate-900">+500</p>
                    <p className="text-xs sm:text-sm text-slate-500">{t('common.companiesTrust')}</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-2 sm:-top-4 -right-2 sm:-right-4 bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-lg border border-slate-100 pointer-events-none">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold">5</div>
                  </div>
                  <div className="flex text-amber-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">{t('common.avgRating')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section id="demo-video" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white heading mb-3 sm:mb-4">
              {t('landing.video.title')}
            </h2>
            <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto">
              {t('landing.video.subtitle')}
            </p>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-800 border border-slate-700">
            <video 
              className="w-full aspect-video"
              controls
              poster="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=675&fit=crop"
              data-testid="demo-video-player"
            >
              <source src="/videos/fortexarh_demo.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="text-center p-4 bg-slate-800/50 rounded-xl">
              <p className="text-2xl sm:text-3xl font-bold text-emerald-400">2 hrs</p>
              <p className="text-xs sm:text-sm text-slate-400">{t('landing.video.avgTime')}</p>
            </div>
            <div className="text-center p-4 bg-slate-800/50 rounded-xl">
              <p className="text-2xl sm:text-3xl font-bold text-emerald-400">100%</p>
              <p className="text-xs sm:text-sm text-slate-400">{t('landing.video.compliance')}</p>
            </div>
            <div className="text-center p-4 bg-slate-800/50 rounded-xl">
              <p className="text-2xl sm:text-3xl font-bold text-emerald-400">70%</p>
              <p className="text-xs sm:text-sm text-slate-400">{t('landing.video.lessQueries')}</p>
            </div>
            <div className="text-center p-4 bg-slate-800/50 rounded-xl">
              <p className="text-2xl sm:text-3xl font-bold text-emerald-400">24/7</p>
              <p className="text-xs sm:text-sm text-slate-400">{t('landing.video.access')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bar — adapts to selected country */}
      <section className="py-6 sm:py-8 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="benefits-bar">
            {/* Item 1 — always generic */}
            <div className="flex items-center gap-2 sm:gap-3 text-white justify-center lg:justify-start">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-100 shrink-0" />
              <span className="text-xs sm:text-sm font-medium">{t('landing.benefits.implementation')}</span>
            </div>

            {/* Item 2 — compliance agency when country profile loaded */}
            <div className="flex items-center gap-2 sm:gap-3 text-white justify-center lg:justify-start" data-testid="benefit-agency">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-100 shrink-0" />
              <span className="text-xs sm:text-sm font-medium">
                {landingCountry.profile?.income_tax?.agency
                  ? `${t('landing.benefits.complianceWith', { defaultValue: 'Cumplimiento' })} ${landingCountry.profile.income_tax.agency}`
                  : t('landing.benefits.secure')}
              </span>
            </div>

            {/* Item 3 — country name + flag when loaded */}
            <div className="flex items-center gap-2 sm:gap-3 text-white justify-center lg:justify-start" data-testid="benefit-country">
              <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-100 shrink-0" />
              <span className="text-xs sm:text-sm font-medium truncate">
                {landingCountry.profile
                  ? `${t('landing.benefits.activeIn', { defaultValue: 'Disponible en' })} ${landingCountry.profile.name}`
                  : t('landing.benefits.anywhere')}
              </span>
            </div>

            {/* Item 4 — native reports count when loaded */}
            <div className="flex items-center gap-2 sm:gap-3 text-white justify-center lg:justify-start" data-testid="benefit-reports">
              <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-100 shrink-0" />
              <span className="text-xs sm:text-sm font-medium">
                {landingCountry.profile?.reports?.length
                  ? `${landingCountry.profile.reports.length} ${t('landing.benefits.nativeReports', { defaultValue: 'reportes nativos' })}`
                  : t('landing.benefits.realtime')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Country Fiscal Section — adapts to selected country */}
      <LandingCountryFiscal profile={landingCountry.profile} t={t} />

      {/* NEW: Geolocation Feature Highlight */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
              🚀 {t('common.new')}
            </span>
          </div>
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              {t('landing.geolocation.title')} <span className="text-emerald-400">{t('landing.geolocation.highlight')}</span>
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto text-sm sm:text-base">
              {t('landing.geolocation.subtitle')}
            </p>
            {landingCountry.profile && (
              <div
                className="mt-4 inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/30 text-emerald-100 px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium"
                data-testid="geo-country-pill"
              >
                <MapPin className="w-3.5 h-3.5" />
                {t('landing.geolocation.availableIn', { defaultValue: 'Disponible en' })} <strong>{landingCountry.profile.name}</strong>
                <span className="opacity-70">·</span>
                <span className="opacity-90">{landingCountry.profile.currency_symbol} {landingCountry.profile.currency}</span>
              </div>
            )}
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-emerald-400/50 transition-all">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="font-semibold text-lg mb-2">{t('landing.geolocation.gps')}</h3>
              <p className="text-slate-400 text-sm">
                {t('landing.geolocation.gpsDesc')}
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-emerald-400/50 transition-all">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-semibold text-lg mb-2">{t('landing.geolocation.realtime')}</h3>
              <p className="text-slate-400 text-sm">
                {t('landing.geolocation.realtimeDesc')}
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-emerald-400/50 transition-all">
              <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="font-semibold text-lg mb-2">{t('landing.geolocation.fraud')}</h3>
              <p className="text-slate-400 text-sm">
                {t('landing.geolocation.fraudDesc')}
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-emerald-400/50 transition-all">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                <Bell className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="font-semibold text-lg mb-2">{t('landing.geolocation.alerts')}</h3>
              <p className="text-slate-400 text-sm">
                {t('landing.geolocation.alertsDesc')}
              </p>
            </div>
          </div>
          
          <div className="mt-10 text-center">
            <p className="text-slate-400 text-sm mb-4" data-testid="geo-ideal-text">
              {landingCountry.profile
                ? t('landing.geolocation.idealIn', {
                    country: landingCountry.profile.name,
                    defaultValue: `Ideal para empresas en ${landingCountry.profile.name} con personal de campo, construcción, delivery, ventas y más.`,
                  })
                : t('landing.geolocation.ideal')}
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                {t('landing.pricing.freeTrial')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 heading mb-3 sm:mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              {t('landing.features.subtitle')}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className={`dashboard-card p-4 sm:p-5 hover:border-emerald-200 animate-fade-in relative ${feature.isNew ? 'border-emerald-300 bg-emerald-50/30' : ''}`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {feature.isNew && (
                  <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {t('common.new')}
                  </span>
                )}
                <div className={`w-10 h-10 ${feature.isNew ? 'bg-emerald-100' : 'bg-emerald-50'} rounded-lg flex items-center justify-center mb-3`}>
                  <feature.icon className={`w-5 h-5 ${feature.isNew ? 'text-emerald-700' : 'text-emerald-600'}`} />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-1 heading">{t(`landing.features.${feature.titleKey}`)}</h3>
                <p className="text-sm text-slate-600">{t(`landing.features.${feature.descKey}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 heading mb-3 sm:mb-4">
            {t('landing.cta.title')}
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-slate-600 mb-6 sm:mb-8 px-4">
            {t('landing.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link to="/register" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-sm sm:text-base px-6 sm:px-8" data-testid="cta-register-btn">
                {t('landing.cta.startTrial')}
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto text-sm sm:text-base px-6 sm:px-8">
                {t('landing.cta.viewPlans')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 heading mb-3 sm:mb-4">
              {t('landing.pricing.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.pricing.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-16">
            {/* Plan Básico */}
            <div className="rounded-xl sm:rounded-2xl border-2 border-slate-200 p-4 sm:p-6 lg:p-8 hover:border-slate-300 transition-all bg-white">
              <div className="text-center mb-4 sm:mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white mb-3 sm:mb-4">
                  <Rocket className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 heading">FortexaRH {t('landing.pricing.basic')}</h3>
                <p className="text-slate-500 mt-1 text-sm sm:text-base">{t('landing.pricing.basicDesc')}</p>
                <div className="mt-3 sm:mt-4">
                  <div className="text-3xl sm:text-4xl font-bold text-slate-900">
                    <span data-testid="price-basic">$5</span>
                    <span className="text-sm font-semibold text-slate-400 align-top ml-1">USD</span>
                  </div>
                  <span className="text-slate-500 text-sm">/{t('landing.pricing.monthly')}</span>
                  <p className="text-xs sm:text-sm text-slate-500 mt-1">
                    + $1.50 USD {t('landing.pricing.perEmployee')}
                  </p>
                </div>
              </div>
              
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{t('landing.pricing.features.upTo')} 50 {t('landing.pricing.features.employees')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />3 {t('landing.pricing.features.usersIncluded')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{t('landing.pricing.features.simpleManagement')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{t('landing.pricing.features.payrollTSS')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{t('landing.pricing.features.attendance')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{t('landing.pricing.features.loans')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{t('landing.pricing.features.basicAccounting')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" /><strong>{t('landing.pricing.features.quickbooks')}</strong></li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-blue-500 flex-shrink-0" /><strong>{t('landing.pricing.features.basicReports')}</strong></li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-400"><X className="w-4 h-4 flex-shrink-0" />{t('landing.pricing.features.expenses')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-400"><X className="w-4 h-4 flex-shrink-0" />{t('landing.pricing.features.employeePortal')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-400"><X className="w-4 h-4 flex-shrink-0" />{t('landing.pricing.features.aiSearch')}</li>
              </ul>
              
              <div className="space-y-2">
                <Link to="/checkout?plan=basic">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-sm sm:text-base" data-testid="buy-basic-btn">{t('landing.pricing.buyPlan')}</Button>
                </Link>
              </div>
            </div>
            
            {/* Plan Pro (Popular) */}
            <div className="rounded-xl sm:rounded-2xl border-2 border-purple-500 p-4 sm:p-6 lg:p-8 relative bg-purple-50 order-first md:order-none">
              <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2">
                <span className="bg-purple-500 text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">{t('common.mostPopular')}</span>
              </div>
              
              <div className="text-center mb-4 sm:mb-6 pt-2">
                <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white mb-3 sm:mb-4">
                  <Zap className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 heading">{t('landing.fortexarhPro')}</h3>
                <p className="text-slate-600 mt-1 text-sm sm:text-base">{t('landing.pricing.proDesc')}</p>
                <div className="mt-3 sm:mt-4">
                  <div className="text-3xl sm:text-4xl font-bold text-slate-900">
                    <span data-testid="price-pro">$10</span>
                    <span className="text-sm font-semibold text-slate-500 align-top ml-1">USD</span>
                  </div>
                  <span className="text-slate-600 text-sm">/{t('landing.pricing.monthly')}</span>
                  <p className="text-xs sm:text-sm text-slate-600 mt-1">
                    + $1.50 USD {t('landing.pricing.perEmployee')}
                  </p>
                </div>
              </div>
              
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{t('landing.pricing.features.upTo')} 200 {t('landing.pricing.features.employees')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />5 {t('landing.pricing.features.usersIncluded')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700 font-medium"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{t('landing.pricing.features.allBasic')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" /><strong>{t('landing.pricing.features.quickbooks')}</strong></li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-purple-500 flex-shrink-0" /><strong>{t('landing.comparison.reports30')}</strong></li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-purple-500 flex-shrink-0" />{t('landing.pricing.features.aiSearch')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-purple-500 flex-shrink-0" />{t('landing.pricing.features.expenses')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-purple-500 flex-shrink-0" />{t('landing.pricing.features.evaluations')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-purple-500 flex-shrink-0" />{t('landing.pricing.features.recruitment')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-purple-500 flex-shrink-0" />{t('landing.pricing.features.employeePortal')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-purple-500 flex-shrink-0" />{t('landing.pricing.features.orgChart')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{t('landing.pricing.features.notifications')}</li>
              </ul>
              
              <div className="space-y-2">
                <Link to="/checkout?plan=pro">
                  <Button className="w-full bg-purple-600 hover:bg-purple-700 text-sm sm:text-base" data-testid="buy-pro-btn">{t('landing.pricing.buyPlan')}</Button>
                </Link>
              </div>
            </div>
            
            {/* Plan Enterprise */}
            <div className="rounded-xl sm:rounded-2xl border-2 border-slate-200 p-4 sm:p-6 lg:p-8 hover:border-slate-300 transition-all bg-gradient-to-br from-amber-50 to-white">
              <div className="text-center mb-4 sm:mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white mb-3 sm:mb-4">
                  <Crown className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 heading">{t('landing.fortexarhEnterprise')}</h3>
                <p className="text-slate-600 mt-1 text-sm sm:text-base">{t('landing.pricing.enterpriseDesc')}</p>
                <div className="mt-3 sm:mt-4">
                  <div className="text-3xl sm:text-4xl font-bold text-slate-900">
                    <span data-testid="price-enterprise">$20</span>
                    <span className="text-sm font-semibold text-slate-500 align-top ml-1">USD</span>
                  </div>
                  <span className="text-slate-600 text-sm">/{t('landing.pricing.monthly')}</span>
                  <p className="text-xs sm:text-sm text-slate-600 mt-1">
                    + $1.50 USD {t('landing.pricing.perEmployee')}
                  </p>
                </div>
              </div>
              
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{t('landing.pricing.features.unlimitedEmployees')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />7 {t('landing.pricing.features.usersIncluded')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700 font-medium"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{t('landing.pricing.features.allPro')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" /><strong>{t('landing.pricing.features.quickbooks')}</strong></li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" /><strong>{t('landing.comparison.reports58')}</strong></li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{t('landing.pricing.features.customReports')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{t('landing.pricing.features.customRoles')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{t('landing.pricing.features.api')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{t('landing.pricing.features.integrations')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{t('landing.pricing.features.workflows')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{t('landing.comparison.contracts')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{t('landing.comparison.eSignature')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{t('landing.comparison.achBank')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{t('landing.pricing.features.branches')}</li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{t('landing.pricing.features.support247')}</li>
              </ul>
              
              <div className="space-y-2">
                <Link to="/checkout?plan=enterprise">
                  <Button className="w-full bg-amber-600 hover:bg-amber-700 text-sm sm:text-base" data-testid="buy-enterprise-btn">{t('landing.pricing.buyPlan')}</Button>
                </Link>
              </div>
            </div>
          </div>
          
          {/* Comparison Table with Collapsible Sections */}
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <h3 className="text-xl sm:text-2xl font-bold text-center mb-6 sm:mb-8 text-slate-900 heading">{t('landing.comparison.title')}</h3>
            <table className="w-full border-collapse min-w-[600px] bg-white">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 sm:py-4 px-2 sm:px-4 font-semibold text-slate-900 text-xs sm:text-sm">{t('landing.comparison.feature')}</th>
                  <th className="text-center py-3 sm:py-4 px-2 sm:px-4 font-semibold text-blue-600 text-xs sm:text-sm">{t('landing.pricing.basic')}</th>
                  <th className="text-center py-3 sm:py-4 px-2 sm:px-4 font-semibold text-purple-600 text-xs sm:text-sm">{t('landing.pricing.pro')}</th>
                  <th className="text-center py-3 sm:py-4 px-2 sm:px-4 font-semibold text-amber-600 text-xs sm:text-sm">{t('landing.pricing.enterprise')}</th>
                </tr>
              </thead>
              <tbody className="text-sm text-slate-700">

                {/* SECTION: Precios y Capacidad - Always open */}
                <tr className="bg-slate-100 border-b border-slate-200 cursor-pointer hover:bg-slate-200 transition-colors" onClick={() => toggleSection('pricing')} data-testid="section-pricing">
                  <td colSpan={4} className="py-2.5 px-4 font-bold text-slate-800 text-sm">
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.pricing ? 'rotate-0' : '-rotate-90'}`} />
                      {t('landing.comparison.sectionPricing')}
                    </div>
                  </td>
                </tr>
                {expandedSections.pricing && (<>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8 text-slate-700">{t('landing.comparison.basePrice')}</td>
                  <td className="py-2.5 px-4 text-center">$5</td>
                  <td className="py-2.5 px-4 text-center">$10</td>
                  <td className="py-2.5 px-4 text-center">$20</td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8 text-slate-700">{t('landing.comparison.pricePerEmployee')}</td>
                  <td className="py-2.5 px-4 text-center">$1.50</td>
                  <td className="py-2.5 px-4 text-center">$1.50</td>
                  <td className="py-2.5 px-4 text-center">$1.50</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8 text-slate-700">{t('landing.comparison.usersIncluded')}</td>
                  <td className="py-2.5 px-4 text-center">3</td>
                  <td className="py-2.5 px-4 text-center">5</td>
                  <td className="py-2.5 px-4 text-center">7</td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8 text-slate-700">{t('landing.comparison.maxEmployees')}</td>
                  <td className="py-2.5 px-4 text-center">50</td>
                  <td className="py-2.5 px-4 text-center">200</td>
                  <td className="py-2.5 px-4 text-center">{t('landing.comparison.unlimited')}</td>
                </tr>
                </>)}

                {/* SECTION: Gestión de RRHH */}
                <tr className="bg-slate-100 border-b border-slate-200 cursor-pointer hover:bg-slate-200 transition-colors" onClick={() => toggleSection('hr')} data-testid="section-hr">
                  <td colSpan={4} className="py-2.5 px-4 font-bold text-slate-800 text-sm">
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.hr ? 'rotate-0' : '-rotate-90'}`} />
                      {t('landing.comparison.sectionHR')}
                    </div>
                  </td>
                </tr>
                {expandedSections.hr && (<>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.employeeManagement')}</td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.attendanceVacations')}</td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.loansModule')}</td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.orgChart')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.selfServicePortal')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8 font-medium text-emerald-700">{t('landing.comparison.contracts')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-amber-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8 font-medium text-emerald-700">{t('landing.comparison.eSignature')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-amber-500 mx-auto" /></td>
                </tr>
                </>)}

                {/* SECTION: Nómina y Finanzas */}
                <tr className="bg-slate-100 border-b border-slate-200 cursor-pointer hover:bg-slate-200 transition-colors" onClick={() => toggleSection('payroll')} data-testid="section-payroll">
                  <td colSpan={4} className="py-2.5 px-4 font-bold text-slate-800 text-sm">
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.payroll ? 'rotate-0' : '-rotate-90'}`} />
                      {t('landing.comparison.sectionPayroll')}
                    </div>
                  </td>
                </tr>
                {expandedSections.payroll && (<>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.payrollTSS')}</td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.accountingNIIF')}</td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.dgiiReports')}</td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8 font-medium text-purple-700">{t('landing.comparison.expensesDouble')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-purple-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-purple-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8 font-medium text-emerald-700">{t('landing.comparison.achBank')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-amber-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8 font-medium text-emerald-700">{t('landing.comparison.workflows')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-amber-500 mx-auto" /></td>
                </tr>
                </>)}

                {/* SECTION: Talento y Evaluaciones */}
                <tr className="bg-slate-100 border-b border-slate-200 cursor-pointer hover:bg-slate-200 transition-colors" onClick={() => toggleSection('talent')} data-testid="section-talent">
                  <td colSpan={4} className="py-2.5 px-4 font-bold text-slate-800 text-sm">
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.talent ? 'rotate-0' : '-rotate-90'}`} />
                      {t('landing.comparison.sectionTalent')}
                    </div>
                  </td>
                </tr>
                {expandedSections.talent && (<>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.evaluations')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.recruitment')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.autoNotifications')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                </>)}

                {/* SECTION: Integraciones y Reportes */}
                <tr className="bg-slate-100 border-b border-slate-200 cursor-pointer hover:bg-slate-200 transition-colors" onClick={() => toggleSection('integrations')} data-testid="section-integrations">
                  <td colSpan={4} className="py-2.5 px-4 font-bold text-slate-800 text-sm">
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.integrations ? 'rotate-0' : '-rotate-90'}`} />
                      {t('landing.comparison.sectionIntegrations')}
                    </div>
                  </td>
                </tr>
                {expandedSections.integrations && (<>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8">{t('landing.features.quickbooks')}</td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.qbDesktop')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-amber-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.sapOracleDynamics')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-amber-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.aiSearch')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-purple-500 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-amber-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8 font-medium text-blue-700">{t('landing.comparison.advancedReportsCenter')}</td>
                  <td className="py-2.5 px-4 text-center text-xs text-slate-500">{t('landing.comparison.basic')}</td>
                  <td className="py-2.5 px-4 text-center text-xs font-medium text-purple-600">{t('landing.comparison.reports30')}</td>
                  <td className="py-2.5 px-4 text-center text-xs font-medium text-amber-600">{t('landing.comparison.reports58')}</td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.customSavedReports')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-amber-500 mx-auto" /></td>
                </tr>
                </>)}

                {/* SECTION: Administración Avanzada */}
                <tr className="bg-slate-100 border-b border-slate-200 cursor-pointer hover:bg-slate-200 transition-colors" onClick={() => toggleSection('admin')} data-testid="section-admin">
                  <td colSpan={4} className="py-2.5 px-4 font-bold text-slate-800 text-sm">
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.admin ? 'rotate-0' : '-rotate-90'}`} />
                      {t('landing.comparison.sectionAdmin')}
                    </div>
                  </td>
                </tr>
                {expandedSections.admin && (<>
                <tr className="border-b border-slate-100">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.customRoles')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-amber-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-2.5 px-4 pl-8">{t('landing.comparison.customAPI')}</td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="py-2.5 px-4 text-center"><Check className="w-4 h-4 text-amber-500 mx-auto" /></td>
                </tr>
                </>)}

                {/* Soporte - Always visible */}
                <tr className="border-t-2 border-slate-200">
                  <td className="py-3 px-4 font-medium text-slate-800">{t('landing.comparison.support')}</td>
                  <td className="py-3 px-4 text-center text-sm text-slate-700">{t('landing.comparison.email')}</td>
                  <td className="py-3 px-4 text-center text-sm text-slate-700">{t('landing.comparison.priority')}</td>
                  <td className="py-3 px-4 text-center text-sm text-slate-700">{t('landing.comparison.dedicated247')}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <p className="text-center text-slate-600 mt-8 text-sm">
            {t('landing.comparison.additionalUsers')}
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16 scroll-reveal">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Star className="w-4 h-4 fill-current" />
              {t('landing.testimonials.badge')}
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 heading mb-3 sm:mb-4">
              {t('landing.testimonials.title')}
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              {t('landing.testimonials.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className={`bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow scroll-reveal scroll-delay-${(index % 4) + 1}`}
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-current" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-emerald-200 mb-3" />
                <p className="text-slate-700 text-base sm:text-lg mb-6 leading-relaxed">
                  &ldquo;{t(`landing.testimonials.items.${testimonial.id}.content`)}&rdquo;
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                  <img 
                    src={testimonial.image}
                    alt={t(`landing.testimonials.items.${testimonial.id}.name`)}
                    className="w-12 h-12 rounded-full object-cover border-2 border-emerald-100"
                  />
                  <div>
                    <p className="font-semibold text-slate-900">{t(`landing.testimonials.items.${testimonial.id}.name`)}</p>
                    <p className="text-sm text-slate-500">{t(`landing.testimonials.items.${testimonial.id}.role`)}</p>
                    <p className="text-sm text-emerald-600 font-medium">{t(`landing.testimonials.items.${testimonial.id}.company`)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-10 sm:mt-12 scroll-reveal">
            <div className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-sm border border-slate-100">
              <div className="flex -space-x-3">
                {testimonials.slice(0, 4).map((testimonial, i) => (
                  <img 
                    key={i}
                    src={testimonial.image}
                    alt=""
                    className="w-8 h-8 rounded-full border-2 border-white object-cover"
                  />
                ))}
              </div>
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">+500</span> {t('common.companiesUse')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 sm:mb-16 scroll-reveal">
            <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <HelpCircle className="w-4 h-4" />
              {t('landing.faq.badge')}
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 heading mb-3 sm:mb-4">
              {t('landing.faq.title')}
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              {t('landing.faq.subtitle')}
            </p>
          </div>
          
          <div className="scroll-reveal">
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {faqIds.map((faqId, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-slate-50 rounded-xl border-none px-6 data-[state=open]:bg-emerald-50 transition-colors"
                >
                  <AccordionTrigger className="text-left text-base sm:text-lg font-medium text-slate-900 hover:no-underline py-5">
                    {t(`landing.faq.items.${faqId}.question`)}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-sm sm:text-base leading-relaxed pb-5">
                    {t(`landing.faq.items.${faqId}.answer`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          
          <div className="text-center mt-10 sm:mt-12 p-6 sm:p-8 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl scroll-reveal-scale scroll-reveal">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
              {t('landing.faq.notFound')}
            </h3>
            <p className="text-emerald-100 mb-6">
              {t('landing.faq.supportReady')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/soporte">
                <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50">
                  <HeadphonesIcon className="w-5 h-5 mr-2" />
                  {t('landing.faq.contactSupport')}
                </Button>
              </Link>
              <a href="mailto:info@fortexaerp.com">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-600">
                  <Mail className="w-5 h-5 mr-2" />
                  {t('common.sendEmail')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 heading mb-3 sm:mb-4">
              {t('landing.contact.title')}
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-slate-600 max-w-2xl mx-auto mb-4 sm:mb-6 px-4">
              {t('landing.contact.subtitle')}
            </p>
            <Link to="/soporte">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-sm sm:text-base">
                <HeadphonesIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {t('landing.contact.supportCenter')}
              </Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-emerald-100 flex items-center justify-center mb-3 sm:mb-4">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2 text-sm sm:text-base">{t('landing.contact.address')}</h3>
              <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                <span className="block"><b>{t('landing.contact.headquartersLabel')}:</b> {t('landing.contact.headquartersValue')}</span>
                <span className="block mt-1"><b>{t('landing.contact.coverageLabel')}:</b> {t('landing.contact.coverageValue')}</span>
              </p>
            </div>
            
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-blue-100 flex items-center justify-center mb-3 sm:mb-4">
                <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2 text-sm sm:text-base">{t('landing.contact.email')}</h3>
              <a href="mailto:info@fortexaerp.com" className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm">
                info@fortexaerp.com
              </a>
            </div>
            
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-purple-100 flex items-center justify-center mb-3 sm:mb-4">
                <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2 text-sm sm:text-base">{t('landing.contact.phone')}</h3>
              <a href="tel:+18096859898" className="text-purple-600 hover:text-purple-700 text-xs sm:text-sm">
                (809) 685-9898
              </a>
            </div>
            
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-amber-100 flex items-center justify-center mb-3 sm:mb-4">
                <Clock3 className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2 text-sm sm:text-base">{t('landing.contact.hours')}</h3>
              <p className="text-slate-600 text-xs sm:text-sm">
                {t('landing.contact.weekdays')}: 9:00 AM - 4:00 PM<br />
                {t('landing.contact.saturday')}: {t('landing.contact.closed')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex flex-col mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_hrpulse-26/artifacts/s3lghfxy_FortexaRH_Logo_transparent.png" 
                  alt="FortexaRH" 
                  className="h-10 sm:h-12 w-auto"
                />
                <p className="text-xs text-slate-400 mt-1">{t('landing.footer.tagline')}</p>
              </div>
              <p className="text-slate-400 text-xs sm:text-sm mb-4 hidden sm:block">
                {t('landing.footer.description')}
              </p>
              <div className="text-slate-400 text-xs sm:text-sm space-y-1 sm:space-y-2">
                <p className="flex items-center gap-2"><Mail className="w-3 h-3 sm:w-4 sm:h-4" /> info@fortexaerp.com</p>
                <p className="flex items-center gap-2"><Phone className="w-3 h-3 sm:w-4 sm:h-4" /> (809) 685-9898</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">{t('landing.footer.product')}</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-slate-400 text-xs sm:text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">{t('landing.footer.features')}</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">{t('landing.footer.pricing')}</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">{t('landing.footer.contact')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">{t('landing.footer.resources')}</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-slate-400 text-xs sm:text-sm">
                <li><Link to="/login" className="hover:text-white transition-colors">{t('landing.footer.login')}</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">{t('landing.footer.createAccount')}</Link></li>
                <li><Link to="/soporte" className="hover:text-white transition-colors">{t('landing.footer.supportCenter')}</Link></li>
                <li><Link to="/accountants-software" className="hover:text-emerald-400 transition-colors">{t('landing.footer.accountantProgram')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">{t('landing.footer.legal')}</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-slate-400 text-xs sm:text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">{t('landing.footer.privacy')}</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">{t('landing.footer.terms')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-6 sm:pt-8 text-center text-slate-400 text-xs sm:text-sm">
            © {new Date().getFullYear()} FortexaRH. {t('landing.footer.rights')}.
          </div>
        </div>
      </footer>
    </div>
  );
}
